import React, { useCallback, useMemo, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { QRCodeCanvas } from 'qrcode.react';
import { t, tf } from '../utils/i18n';
import { buildBinanceDepositQrValue } from '../utils/binanceDepositQr';

export default function BinanceDepositPanel({
  info,
  language,
  translationPrefix,
  disclaimerClassName = 'text-muted',
  titleClassName = 'h6'
}) {
  const qrWrapRef = useRef(null);
  const qrValue = useMemo(
    () => buildBinanceDepositQrValue(info),
    [info?.address, info?.tag]
  );

  const downloadQr = useCallback(() => {
    const canvas = qrWrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `binance-usdt-${info?.reference || 'deposit'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [info?.reference]);

  if (!info) return null;

  return (
    <>
      <h2 className={titleClassName}>{t(`${translationPrefix}.binanceInstructionsTitle`, language)}</h2>

      {qrValue && (
        <div className="binance-deposit-qr mb-4">
          <p className="small mb-2">{t(`${translationPrefix}.binanceScanQr`, language)}</p>
          <div className="binance-deposit-qr-frame" ref={qrWrapRef}>
            <QRCodeCanvas
              value={qrValue}
              size={220}
              level="M"
              includeMargin
              bgColor="#ffffff"
              fgColor="#000000"
              role="img"
              aria-label={t(`${translationPrefix}.binanceScanQr`, language)}
            />
          </div>
          <div className="d-flex flex-wrap gap-2 justify-content-center mt-3">
            <Button variant="outline-secondary" size="sm" onClick={downloadQr}>
              {t(`${translationPrefix}.binanceDownloadQr`, language)}
            </Button>
          </div>
          <p className="small binance-deposit-qr-hint mb-0 mt-2">
            {t(`${translationPrefix}.binanceQrHint`, language)}
          </p>
        </div>
      )}

      <p className="small mb-2">
        <strong>{t(`${translationPrefix}.binanceReference`, language)}</strong> {info.reference}
      </p>
      <p className="small mb-2">
        <strong>{t(`${translationPrefix}.binanceNetwork`, language)}</strong> {info.network}
      </p>
      <p className="small mb-2">
        <strong>{t(`${translationPrefix}.binanceAmount`, language)}</strong> {info.amount} USDT
      </p>
      <p className={`small ${disclaimerClassName} mb-2`}>
        {tf(
          `${translationPrefix}.binanceAmountHint`,
          { usd: String(info.amountLabelUsd) },
          language
        )}
      </p>
      <p className="small mb-1">
        <strong>{t(`${translationPrefix}.binanceAddress`, language)}</strong>
      </p>
      <code className="small d-block user-select-all mb-3">{info.address}</code>
      {info.tag && (
        <>
          <p className="small mb-1">
            <strong>{t(`${translationPrefix}.binanceTag`, language)}</strong>
          </p>
          <code className="small d-block user-select-all mb-3">{info.tag}</code>
        </>
      )}
      <p className={`small ${disclaimerClassName} mb-0`}>
        {t(`${translationPrefix}.binancePolling`, language)}
      </p>
    </>
  );
}
